import type { Pinia, PiniaPlugin, StateTree, Store } from 'pinia';
import { createPinia, getActivePinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { toRaw } from 'vue';

const pinia = createPinia();

export interface ExtendedPinia extends Pinia {
    _s: Map<string, Store>;
}

const isStore = (value: unknown): value is Store =>
    !!value && typeof value === 'object' && typeof (value as { $id?: unknown }).$id === 'string';

const cloneState = <T>(value: T): T => structuredClone(value);

/**
 * Snapshot the serialisable, self-owned slice of a freshly created store's state.
 *
 * Two kinds of keys are skipped:
 *  - nested Pinia stores (e.g. the per-list `*-sorting` store several stores expose
 *    as `sortStore`): each is registered in `pinia._s` in its own right and is
 *    reset directly by `$ResetPinia().all()`. Cloning/reassigning it here would
 *    both fail (it holds functions) and detach the live reference.
 *  - anything `structuredClone` cannot copy (class instances, etc.): left out of
 *    the reset scope rather than crashing the snapshot.
 */
function snapshotResettableState(state: StateTree): Record<string, unknown> {
    const snapshot: Record<string, unknown> = {};
    for (const key of Object.keys(state)) {
        const raw = toRaw(state[key]);
        if (isStore(raw)) continue;
        try {
            snapshot[key] = cloneState(raw);
        } catch {
            // Non-cloneable value (e.g. a class instance): not part of the reset scope.
        }
    }
    return snapshot;
}

/**
 * Gives every *setup* store a working `$reset()`.
 *
 * Pinia only implements `$reset()` for option stores; on a setup store it throws
 * ("does not implement $reset()"). This plugin captures each store's initial state
 * at creation and restores it on reset, so `$ResetPinia().all()` can actually wipe
 * a previous user's in-memory data on login/logout (customers, invoices, …).
 *
 * Stores are skipped when:
 *  - they are option stores (Pinia already gives them a working `$reset`), or
 *  - they implement their own `$reset` action. A few stores keep their actual data
 *    in a closure-private ref (exposed only through a computed), which the generic
 *    snapshot cannot see; those provide a hand-written `$reset` and own their reset.
 *
 * Registered BEFORE persisted-state so the snapshot is the code default rather than
 * a value rehydrated from localStorage — a reset must clear persisted data too.
 */
export const resetStatePlugin: PiniaPlugin = ({ store, options }) => {
    const isOptionStore = typeof (options as { state?: unknown }).state === 'function';
    const hasOwnReset = !!options.actions && '$reset' in options.actions;
    if (isOptionStore || hasOwnReset) return;

    const initialState = snapshotResettableState(store.$state);

    store.$reset = () => {
        store.$patch(($state) => {
            for (const key of Object.keys(initialState)) {
                ($state as Record<string, unknown>)[key] = cloneState(initialState[key]);
            }
        });
    };
};

pinia.use(resetStatePlugin);
pinia.use(piniaPluginPersistedstate);

/**
 * Stores excluded from the bulk `all()` reset. Each owns its own lifecycle and a
 * blanket reset would regress it:
 *  - `auth`: signOut() nulls user/authToken/activeCompanyId by hand, and
 *    activeCompanyId is persisted + rehydrated on boot, so resetting auth here
 *    would drop the user's selected company on every init().
 *  - `syncQueue`: backed by localStorage; signOut() calls clearAll(), which wipes
 *    both the in-memory queue and its stored copy. A generic reset would not touch
 *    localStorage and could fight the offline-sync lifecycle (the queue is replayed
 *    by the service worker / online watcher, all auth-gated).
 *  - `referenceData`: persisted, company-keyed cache of form lookup data (BTW
 *    codes). A reset clears persisted data too (see resetStatePlugin), which would
 *    wipe the cache during init()'s `$ResetPinia().all()` — exactly when an offline
 *    boot needs it to render the create forms. Non-sensitive, so it is kept (not
 *    cleared on signOut) to survive across sessions.
 */
const RESET_EXCLUDED_STORES = new Set(['auth', 'syncQueue', 'referenceData']);

/**
 * Returns an object of per-store reset functions plus an `all()` that resets every
 * registered store except the excluded ones. Call as `$ResetPinia().all()`.
 *
 * Note: calling `$ResetPinia()` bare resets nothing — you must invoke `.all()`
 * (or a named entry). This is intentional so reset is always explicit.
 */
export const $ResetPinia = (): Record<string | 'all', () => void> => {
    const activePinia = getActivePinia() as ExtendedPinia;

    if (!activePinia) {
        throw new Error('There are no stores');
    }

    const resetStores: Record<string, () => void> = {};

    activePinia._s.forEach((store, name) => {
        resetStores[name] = () => store.$reset();
    });

    resetStores.all = () => {
        activePinia._s.forEach((store, name) => {
            if (RESET_EXCLUDED_STORES.has(name)) return;
            store.$reset();
        });
    };

    return resetStores;
};

export default pinia;
